import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Settings as SettingsIcon, Upload, Image as ImageIcon, Lock, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function Settings() {
  const { user } = useAuth();
  const [schoolInfo, setSchoolInfo] = useState({
    school_name: 'School Dashboard',
    logo_url: null,
    has_logo: false
  });
  const [schoolName, setSchoolName] = useState('');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoPreview, setLogoPreview] = useState(null);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    fetchSchoolInfo();
  }, []);

  const fetchSchoolInfo = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/school-info`);
      setSchoolInfo(data);
      setSchoolName(data.school_name);
      if (data.has_logo) {
        setLogoPreview(`${BACKEND_URL}${data.logo_url}?t=${Date.now()}`);
      }
    } catch (error) {
      console.error('Error fetching school info:', error);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      await axios.post(
        `${BACKEND_URL}/api/settings/logo`,
        formData,
        {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' }
        }
      );

      toast.success('School logo uploaded successfully');
      fetchSchoolInfo();
      e.target.value = ''; // Reset input
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!window.confirm('Are you sure you want to remove the school logo? The default icon will be displayed.')) {
      return;
    }

    try {
      await axios.delete(
        `${BACKEND_URL}/api/settings/logo`,
        { withCredentials: true }
      );
      toast.success('School logo removed successfully');
      setLogoPreview(null);
      fetchSchoolInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove logo');
    }
  };

  const handleSchoolNameUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.put(
        `${BACKEND_URL}/api/settings/school-name`,
        { school_name: schoolName },
        { withCredentials: true }
      );
      toast.success('School name updated successfully');
      fetchSchoolInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update school name');
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('New passwords do not match');
      return;
    }
    
    if (passwordData.new_password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await axios.put(
        `${BACKEND_URL}/api/auth/change-password`,
        {
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        },
        { withCredentials: true }
      );
      toast.success('Password changed successfully');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  const isAdmin = user.role === 'admin';

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-[#0F172A] tracking-tight" style={{ fontFamily: 'Manrope' }}>
          {isAdmin ? 'Settings' : 'Account Settings'}
        </h1>
        <p className="text-[#64748B] mt-2">
          {isAdmin ? "Manage your school's branding and settings" : "Manage your account settings"}
        </p>
      </div>

      <div className="grid gap-6 max-w-4xl">
        {/* School Logo Upload - Admin Only */}
        {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="w-5 h-5 text-[#667EEA]" />
              School Logo
            </CardTitle>
            <CardDescription>Upload your school's logo. It will appear in the sidebar and branding.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {logoPreview && (
                <div className="flex items-center justify-center p-6 bg-[#F9FAFB] border-2 border-dashed border-[#E2E8F0] rounded-lg">
                  <img
                    src={logoPreview}
                    alt="School Logo"
                    className="max-h-32 object-contain"
                    data-testid="logo-preview"
                  />
                </div>
              )}
              
              <input
                type="file"
                id="logo-upload"
                className="hidden"
                accept="image/*"
                onChange={handleLogoUpload}
                disabled={uploadingLogo}
              />
              
              <Button
                onClick={() => document.getElementById('logo-upload').click()}
                disabled={uploadingLogo}
                className="w-full bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
                data-testid="upload-logo-btn"
              >
                <Upload className="w-4 h-4 mr-2" />
                {uploadingLogo ? 'Uploading...' : schoolInfo.has_logo ? 'Change Logo' : 'Upload Logo'}
              </Button>

              {logoPreview && (
                <Button
                  onClick={handleRemoveLogo}
                  variant="outline"
                  className="w-full border-red-300 text-red-600 hover:bg-red-50"
                  data-testid="remove-logo-btn"
                >
                  Remove Logo
                </Button>
              )}
              
              <p className="text-sm text-[#64748B]">
                Recommended: Square image (500x500px or larger). Max size: 5MB. Formats: JPG, PNG, WEBP, SVG
              </p>
            </div>
          </CardContent>
        </Card>
        )}

        {/* School Name - Admin Only */}
        {isAdmin && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <SettingsIcon className="w-5 h-5 text-[#667EEA]" />
              School Name
            </CardTitle>
            <CardDescription>Customize your school's name displayed in the dashboard</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSchoolNameUpdate} className="space-y-4">
              <div>
                <Label htmlFor="school-name">School Name</Label>
                <Input
                  id="school-name"
                  data-testid="school-name-input"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  placeholder="e.g., Springfield High School"
                  required
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                data-testid="save-school-name-btn"
                className="bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
              >
                Save School Name
              </Button>
            </form>
          </CardContent>
        </Card>
        )}

        {/* Change Password - Available to All Users */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-[#667EEA]" />
              Change Password
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <Label htmlFor="current-password">Current Password</Label>
                <Input
                  id="current-password"
                  type="password"
                  data-testid="current-password-input"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({ ...passwordData, current_password: e.target.value })}
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="new-password-change">New Password</Label>
                <Input
                  id="new-password-change"
                  type="password"
                  data-testid="new-password-change-input"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({ ...passwordData, new_password: e.target.value })}
                  required
                  minLength={6}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input
                  id="confirm-password"
                  type="password"
                  data-testid="confirm-password-input"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({ ...passwordData, confirm_password: e.target.value })}
                  required
                  minLength={6}
                  className="mt-1"
                />
              </div>
              <Button
                type="submit"
                data-testid="change-password-btn"
                className="bg-gradient-to-r from-[#667EEA] to-[#764BA2] hover:from-[#764BA2] hover:to-[#667EEA]"
              >
                Change Password
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
